import axios from 'axios';
import { ServiceStatusEnum } from './serviceStatusEnum';
import { currentProfileCache } from '@/modules/utils/currentProfileCache.js';

/** GAPI `field` → form key when it is not `account_*` stripped + snake_case → camelCase (e.g. account_phone → phone, but we need homePhone). */
const GAPI_FIELD_FORM_OVERRIDES = {
  account_phone: 'homePhone',
  account_mobile: 'mobilePhone'
};

/** Dotted/slash GAPI paths → checkout field name (`passengers[].fields` or `passengers[].address.fields`). */
const GAPI_FIELD_EXACT_ALIASES = {
  'address/state/id': 'county',
  'address/country/id': 'country'
};

function gapiFieldToFormField(apiField) {
  if (!apiField) {
    return null;
  }
  const exact = GAPI_FIELD_EXACT_ALIASES[apiField];
  if (exact) {
    return exact;
  }
  if (GAPI_FIELD_FORM_OVERRIDES[apiField]) {
    return GAPI_FIELD_FORM_OVERRIDES[apiField];
  }
  const rest = apiField.startsWith('account_') ? apiField.slice(8) : apiField;
  if (!rest) {
    return null;
  }
  return rest.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

const CREATE_BOOKING_INLINE_FIELD_MESSAGE = 'Please check this field.';

/** Shown when create-booking validation targets the email field (GAPI e.g. account_email). */
const CREATE_BOOKING_EMAIL_FIELD_MESSAGE =
  'This email address isn\'t valid. Please try another one.';

function createBookingMessageForField(formField) {
  if (formField === 'email') {
    return CREATE_BOOKING_EMAIL_FIELD_MESSAGE;
  }
  return CREATE_BOOKING_INLINE_FIELD_MESSAGE;
}

const CONTACT_MODAL_FORM_FIELDS_AB = new Set(['email', 'firstName', 'lastName']);

/** `room-i-passenger-j-address` — must match `Passenger.vue` session keys. */
function sessionStorageAddressKeyForPassenger(state, passengerIndex) {
  let currentIndex = 0;
  for (let i = 0; i < state.rooms.length; i++) {
    const room = state.rooms[i];
    for (let j = 0; j < room.passengers.length; j++) {
      if (currentIndex === passengerIndex) {
        return `room-${i}-passenger-${j}-address`;
      }
      currentIndex++;
    }
  }
  return null;
}

/** After a 400 on county/country, drop those keys from persisted address so restore cannot show stale geo. */
function stripAddressCountyCountryFromSessionStorage(state, passengerIndex, fieldsToRemove) {
  if (typeof sessionStorage === 'undefined' || !fieldsToRemove?.size) {
    return;
  }
  const key = sessionStorageAddressKeyForPassenger(state, passengerIndex);
  if (!key) {
    return;
  }
  const raw = sessionStorage.getItem(key);
  if (!raw) {
    return;
  }
  try {
    const parsed = JSON.parse(raw);
    fieldsToRemove.forEach(f => {
      if (f === 'county' || f === 'country') {
        delete parsed[f];
      }
    });
    sessionStorage.setItem(key, JSON.stringify(parsed));
  } catch (e) {
    console.error('stripAddressCountyCountryFromSessionStorage:', e);
  }
}

/** True when a Searchable (nationality, county, etc.) must not be sent to create-booking. */
function isInvalidSearchableForBooking(value) {
  return (
    value == null ||
    typeof value === 'string' ||
    (typeof value === 'object' && !String(value.id || '').trim())
  );
}

/** Omit invalid Searchable fields before create-booking (API expects { id, name } or omitted). */
function normalizePassengerAddressForBooking(passenger) {
  const result = { ...passenger };
  const nationality = result.nationality;

  if (isInvalidSearchableForBooking(nationality)) {
    delete result.nationality;
  }

  if (!result.address) {
    return result;
  }

  const address = { ...result.address };
  const county = address.county;

  if (isInvalidSearchableForBooking(county)) {
    delete address.county;
  }

  if (isInvalidSearchableForBooking(address.country)) {
    delete address.country;
  }

  return { ...result, address };
}

/** Passengers that would lose nationality during normalize — needs soft-decline UX, not a silent strip + 500. */
function findMissingNationalityFieldErrors(passengers) {
  const errors = [];
  for (let i = 0; i < passengers.length; i++) {
    if (isInvalidSearchableForBooking(passengers[i].nationality)) {
      const nationality = passengers[i].nationality;
      const value =
        typeof nationality === 'object' && nationality?.name ? String(nationality.name) : '';
      errors.push({
        field: 'account_nationality',
        passengerIndex: i,
        value
      });
    }
  }
  return errors;
}

function searchableDisplayName(value) {
  if (typeof value === 'object' && value?.name) {
    return String(value.name);
  }
  if (typeof value === 'string') {
    return value;
  }
  return '';
}

/** Country name without a GAPI id would 500 in CreateBooking (`Country.Id`). Surface it on the field instead. */
function findMissingCountryFieldErrors(passengers) {
  const errors = [];
  for (let i = 0; i < passengers.length; i++) {
    const address = passengers[i].address;
    if (!address) {
      continue;
    }
    const country = address.country;
    const hasCountry = country != null && country !== '';
    const hasAddressContent =
      address.postCode || address.town || address.addressLines?.[0] || address.addressLines?.[1];
    if (!hasCountry && !hasAddressContent) {
      continue;
    }
    if (isInvalidSearchableForBooking(country)) {
      errors.push({
        field: 'country',
        passengerIndex: i,
        value: searchableDisplayName(country)
      });
    }
  }
  return errors;
}

function applyCreateBookingFieldErrorItems({ state, commit }, items) {
  if (!items?.length) {
    return false;
  }

  const maxIdx = Math.max(0, state.passengers.length - 1);
  const touched = new Set();
  const contactModalOpenIndexes = [];
  /** Passenger index → which address geo keys to strip from sessionStorage after 400 */
  const geoFieldsToStripByPassenger = new Map();

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    let idx = item.passengerIndex;
    if (typeof idx !== 'number' || Number.isNaN(idx)) {
      idx = 0;
    }
    idx = Math.min(Math.max(0, idx), maxIdx);
    touched.add(idx);

    const formField = gapiFieldToFormField(item.field);
    if (!formField) {
      continue;
    }

    const fallbackValue =
      item.value != null && typeof item.value !== 'object' ? String(item.value) : '';

    commit('setPassengerFieldApiMessage', {
      passengerIndex: idx,
      fieldName: formField,
      message: createBookingMessageForField(formField),
      fallbackValue
    });

    if (formField === 'county' || formField === 'country') {
      if (!geoFieldsToStripByPassenger.has(idx)) {
        geoFieldsToStripByPassenger.set(idx, new Set());
      }
      geoFieldsToStripByPassenger.get(idx).add(formField);
    }

    if (CONTACT_MODAL_FORM_FIELDS_AB.has(formField)) {
      contactModalOpenIndexes.push(idx);
    }
  }

  geoFieldsToStripByPassenger.forEach((fields, pIdx) => {
    stripAddressCountyCountryFromSessionStorage(state, pIdx, fields);
  });

  /* Reopen affected passengers + last submitter so summary does not look "saved" after a validation failure. */
  touched.add(maxIdx);
  commit('reopenPassengerDetailEditors', Array.from(touched));
  if (contactModalOpenIndexes.length) {
    commit('bookingValidationPromptContactModal', contactModalOpenIndexes);
  }
  return true;
}

function applyCreateBookingFieldErrors({ state, commit }, axiosError) {
  const res = axiosError.response;
  const payload = res?.data;
  const items = Array.isArray(payload?.fieldErrors)
    ? payload.fieldErrors
    : Array.isArray(payload?.FieldErrors)
      ? payload.FieldErrors
      : null;

  if (res?.status !== 400 || !items?.length) {
    return false;
  }

  return applyCreateBookingFieldErrorItems({ state, commit }, items);
}

export default {
  getAddons({
    state,
    commit,
    dispatch
  }) {
    commit('setLoading', true);

    return axios
      .get(state.data.addons.dataUrl, {
        params: {
          departureId: state.quote.departureId,
          filo: state.quote.filo,
          transportCode: state.quote.departureTransportCode,
          tourCode: state.quote.tourCode
        }
      })
      .then(response => {
        commit('setAddonData', response.data);
        const addOns =
          response.data && Array.isArray(response.data.addOns)
            ? response.data.addOns
            : [];
        if (addOns.length === 0) {
          commit('setLoadingStepOverride', 'travellers');
          return dispatch('getDepartureDetails', {
            retainLoaderUntilCallerClears: true
          }).then(() => {
            commit('nextStep', 2);
            commit('setLoading', false);
          });
        }
        commit('setLoading', false);
      })
      .catch(() => {
        commit('setLoading', false);
        commit('setError', true);
      });
  },

  getDepartureDetails(
    { state, commit },
    options = {}
  ) {
    const { retainLoaderUntilCallerClears } = options;

    commit('setLoading', true);

    return axios
      .get(state.data.flights.dataUrl, {
        params: {
          departureId: state.quote.departureId,
          filo: state.quote.filo,
          transportCode: state.quote.departureTransportCode
        }
      })
      .then(response => {
        commit('setDepartureDetails', response.data);
        commit('setBookingFlights');
        if (!retainLoaderUntilCallerClears) {
          commit('setLoading', false);
        }
      })
      .catch(() => {
        commit('setLoading', false);
        commit('setError', true);
      });
  },

  getAvailableRooms({
    state,
    commit
  }) {
    return axios
      .get(state.data.travellers.roomsDataUrl, {
        params: {
          departureId: state.quote.departureId,
          filo: state.quote.filo,
          transportCode: state.quote.departureTransportCode
        }
      })
      .then(response => {
        commit('setAvailableRooms', response.data.rooms);
        commit('setMaxiumumPassengers', response.data.maxAvailable);
        commit('setData', {
          priceBreakdown: {
            ...state.data.priceBreakdown,
            localPayments: response.data.localPayments
          }
        });
      })
      .catch(() => {
        commit('setError', true);
      });
  },

  rooms({
    state,
    commit,
    getters,
    dispatch
  }) {
    commit('setLoading', true);

    return axios
      .post(state.data.travellers.submitUrl, {
        ...state.quote,
        passengers: getters.roomPassengers,
        donatingTravellers: getters.donatingTravellers,
        donationExtraId: getters.donationExtraId,
        donationAllocationType: getters.donationAllocationType,
        donationForBooking: getters.donationPerBooking === true
      })
      .then(response => {
        commit('setQuote', response.data);
        commit('setQuoteDonations');
        dispatch('hydrateRoomsFromApi');
        commit('resetExcursions');
        commit('resetPassengers');
        commit('setSubtotalMismatch', getters.runningSubtotal);
        commit('setDonationMade', response.data.donationMade);
        commit('setLoading', false);
      })
      .catch(() => {
        commit('setError', true);
      });
  },

  /**
   * we hydrate rooms from the returned API data
   * this ensures we get passengerIds in our frontend rooms array
   * which allows us to keep "passengerDetails" when navigating back and forward through the checkout
   */
  hydrateRoomsFromApi({
    state,
    commit,
    rootGetters
  }) {
    state.rooms.forEach(room => {
      // loop the room passengers
      // match the passenger to quote.passengers using (roomId / firstName / lastName)
      room.passengers.forEach(roomPassenger => {
        const roomPassengerData = rootGetters.formDataFlat(roomPassenger);
        const matchingQuotePassenger = state.quote.passengers.find(quotePassenger => {
          return quotePassenger.roomId === room.id && quotePassenger.firstName === roomPassengerData.firstName && quotePassenger.lastName === roomPassengerData.lastName;
        });
        if (matchingQuotePassenger) {
          commit('setRoomPassengerId', {
            roomPassenger,
            passengerId: matchingQuotePassenger.passengerId
          });
        }
      });
    });
  },

  getExcursions(
    { state, commit },
    options = {}
  ) {
    const showStepLoader = options.showStepLoader === true;
    if (showStepLoader) {
      commit('setExcursionsLoading', true);
    }

    return axios
      .get(state.data.excursions.dataUrl, {
        params: {
          departureId: state.quote.departureId
        }
      })
      .then(response => {
        commit('setExcursionData', response.data);
      })
      .catch(() => {
        commit('setError', true);
      })
      .finally(() => {
        if (showStepLoader) {
          commit('setExcursionsLoading', false);
        }
      });
  },

  getDonations({
    state,
    getters,
    commit
  }) {
    commit('setLoading', true);
    return axios
      .get(state.data.donations.dataUrl, {
        params: {
          departureId: getters.departureId
        }
      })
      .then(response => {
        commit('setDonationsData', response.data);
        commit('setLoading', false);
      })
      .catch(() => {
        commit('setError', true);
      });
  },

  excursions({
    state,
    commit,
    getters
  }) {
    commit('setLoading', true);

    return axios
      .post(state.data.excursions.submitUrl, {
        ...state.quote,
        excursions: getters.excursions
      })
      .then(response => {
        commit('setQuote', response.data);
        commit('setQuoteDonations');
        commit('setSubtotalMismatch', getters.runningSubtotal);
        commit('setLoading', false);
      })
      .catch(() => {
        commit('setError', true);
      });
  },

  handleExcursionChange({
    state,
    commit
  }) {
    // compare state.excursions to state.quote.excursions
    if (JSON.stringify(state.excursions) !== JSON.stringify(state.quote.excursions)) {
      // If different, reset payment and reopen passenger information
      commit('resetPayment');

      // for each passenger index
      for (let i = 0; i < state.passengers.length; i++) {
        commit('unsetHasBeenSubmitted', ['checkout', 'passengers', i], {
          root: true
        });
        commit('unsetSummaryView', ['checkout', 'passengers', i], {
          root: true
        });
      }

      axios.cancel('passengers');
    }
  },

  async getPassengerResources({ state, commit }) {
    if (state.passengerResources?.nationalities?.length > 0) {
      return Promise.resolve();
    }

    commit('setPassengerResourcesLoading', true);
    return axios
      .get(state.data.passengerDetails.resourcesUrl)
      .then(response => {
        if(response.data.success) {
          commit('setPassengerResources', response.data.result);
        }
      })
      .catch(() => {
        commit('setError', true);
      })
      .finally(() => {
        commit('setPassengerResourcesLoading', false);
      });
  },

  getLoggedInPassengerData({
    state,
    commit,
    dispatch
  }) {
    // Check for cached profile data
    const cachedProfile = currentProfileCache.get();

    if (cachedProfile) {
      // Use cached profile data
      commit('setloggedInPassengerData', cachedProfile);
      dispatch('validateLeadTravelCredits');
      return Promise.resolve();
    }

    // If no cached data, fetch from API
    return axios
      .get(state.data.login.userDetailsUrl)
      .then(response => {
        commit('setloggedInPassengerData', response.data);
        // Cache the profile data
        currentProfileCache.set(response.data);
        dispatch('validateLeadTravelCredits');
      })
      .catch(error => {
        console.warn(error);
      });
  },

  /** the cached profile can be stale, so only credits GAPI confirms right now are shown before the booking exists */
  validateLeadTravelCredits({
    state,
    commit
  }) {
    if (!state.travelCredits.length || !state.data.booking?.leadTravelCreditsUrl) {
      return Promise.resolve();
    }

    return axios
      .get(state.data.booking.leadTravelCreditsUrl)
      .then(response => {
        commit('setValidatedTravelCredits', response.data);
      })
      .catch(error => {
        console.warn(error);
      });
  },

  async getBookingData({
    state,
    commit
  }) {
    return axios
      .get(state.data.booking.dataUrl, {
        params: {
          bookingReference: state.bookingId
        }
      })
      .then(response => {
        commit('setBookingData', response.data);
      })
      .catch(error => {
        console.warn(error);
      });
  },

  updateAutoBalance({
    state,
    getters,
    commit
  }) {
    const bookingId = state.bookingId;
    const isDepositSelected = getters.paymentData.amountToPay === getters.totalDeposit;
    const noAutoFinalPayment = !getters.depositPaymentAllowed || !isDepositSelected ? true : Boolean(!(state.takeFinalBalance));

    return axios
      .post(state.data.booking.autoBalanceSubmitUrl, {
        bookingReference: bookingId,
        noAutoFinalPayment: noAutoFinalPayment 
      })
      .then(response => {
        commit('setBooking', response.data);
      })
      .catch(error => {
        console.warn(error);
      });
  },

  generateToken({
    state
  }) {
    return axios
      .post(state.data.login.generateTokenUrl)
      .then(response => {        
        window.__RequestVerificationToken = response.data;
      })
      .catch(error => {
        console.warn(error);
      });
  },

  async passengers({
    state,
    commit,
    getters,
    dispatch
  }) {

    try {
      commit('setPassengerLoading', true);
      commit('clearBookingValidationFeedback');

      const roomPassengerMap = new Map(
        getters.roomPassengers.map(p => [p.passengerGuid, p])
      );

      const passengersWithDetails = getters.passengersDetails
        .filter(p => roomPassengerMap.has(p.passengerGuid))
        .map(p => {
          const { fields, ...roomPassengerWithoutFields } = roomPassengerMap.get(p.passengerGuid);

          return {
            ...roomPassengerWithoutFields,
            ...p,
          };
        });

      const missingNationalityErrors = findMissingNationalityFieldErrors(passengersWithDetails);
      if (missingNationalityErrors.length) {
        applyCreateBookingFieldErrorItems({ state, commit }, missingNationalityErrors);
        commit('setPassengerLoading', false);
        return false;
      }

      const missingCountryErrors = findMissingCountryFieldErrors(passengersWithDetails);
      if (missingCountryErrors.length) {
        applyCreateBookingFieldErrorItems({ state, commit }, missingCountryErrors);
        commit('setPassengerLoading', false);
        return false;
      }

      const normalizedPassengersWithDetails = passengersWithDetails.map(p =>
        normalizePassengerAddressForBooking(p)
      );
        
      const groupedByRoom = normalizedPassengersWithDetails.reduce((acc, passenger) => {
          const { roomKey } = passenger;
          if (!acc[roomKey]) acc[roomKey] = [];
          acc[roomKey].push(passenger);
          return acc;
      }, {});

      const roomsData = Object.entries(groupedByRoom).map(([roomKey, passengers]) => ({
          roomKey,
          passengers: passengers.map(passenger => ({
            ...passenger,
            travelCredits: passenger.lead ? (state.travelCredits || []) : (passenger.travelCredits || [])
          }))
      }));

      const tour = `${state.tours[0].id} - ${state.tours[0].name} - Departing ${state.bookingFlights && state.bookingFlights.length > 0 ? state.bookingFlights[0].departureDate : state.departureDetails.departureNoFlights.date}`;

      const response = await axios.post(state.data.booking.submitUrl, {
        currency: state.data.currency,
        productId: getters.productId,
        tour: tour,
        donation: {
          feeId: getters.donationFeeId,
          feePrice: getters.donationFeePrice,
          donatingPassengerGuids: getters.donatingPassengerGuids || []
        },
        roomsData: roomsData,
        excursions: getters.excursionsPassengerGuids || []
      });

      const bookingId = response.data.departureServices[0].booking.id;
      const departureServices = response.data.departureServices;
      const activityServices = response.data.activityServices;
      const feeServices = response.data.feeServices;
      const marketingPreferences = response.data.marketingPreferences;
      const failedExcursions = response.data.failedExcursions || [];
      
      passengersWithDetails.forEach(pd => {
        commit('updatePassengerDirectData', {
          passengerGuid: pd.passengerGuid,
          data: {
            firstName: pd.firstName,
            lastName: pd.lastName,
            email: pd.email,
            phone: pd.mobilePhone || pd.homePhone || '',
            gtmPassengerId: pd.passengerId || '',
            addressTown: pd.address?.town || '',
            addressCountry: pd.address?.country || '',
            addressPostCode: pd.address?.postCode || '',
          }
        });
      });

      commit('setBookingId', bookingId);
      commit('setDepartureServices', departureServices);
      commit('setActivityServices', activityServices);
      commit('setFeeServices', feeServices);
      commit('setMarketingPreferences', marketingPreferences);
      commit('setFailedExcursions', failedExcursions);
      commit('setAppliedTravelCredits', response.data.appliedTravelCredits || []);
      commit('setTravelCreditError', response.data.travelCreditApplyError || '');

      commit('setPaymentLoading', true);
      await dispatch('getBookingData');

      const onRequestStatus = state.departureServices.find(ds => ds.status === ServiceStatusEnum.OnRequest);
      if (onRequestStatus) {
        commit('setOnRequestError', true);
      }

      const canPay = state.departureServices.every(ds => ds.status === ServiceStatusEnum.Option);
      const paymentData = {
        canPay: canPay
      };
      commit('setPayment', paymentData);
      commit('setPaymentLoading', false);
      commit('setPassengerLoading', false);
      return true;

    } catch (error) {
      if (applyCreateBookingFieldErrors({ state, commit }, error)) {
        commit('setPassengerLoading', false);
        return false;
      }
      commit('setError', true);
      commit('setPassengerLoading', false);
      return false;
    }
  },

  updateMarketingPreferences({
    state,
    commit
  }) {
    if (state.marketingPreferencesSubmitted) {
      return Promise.resolve();
    }

    const marketingPreferences = state.marketingPreferences;
    
    if (!marketingPreferences || marketingPreferences.length === 0) {
      return Promise.resolve();
    }

    return axios
      .post(state.data.booking.marketingPreferencesSubmitUrl, marketingPreferences)
      .then(response => {
        commit('setMarketingPreferencesSubmitted', true);
        return response;
      })
      .catch(error => {
        throw error;
      });
  },

  payment({ commit, state, getters }) {
    commit('setPaymentLoading', true);

    const isDepositSelected = getters.paymentData.amountToPay === getters.totalDeposit;
    const autoBalance = getters.depositPaymentAllowed && isDepositSelected && state.takeFinalBalance;

    return axios
      .post('/umbraco/api/payment', {
        bookingId: state.bookingId,
        amount: state.amountSelected,
        currency: state.quote.currency,
        cultureCode: state.quote.cultureCode,
        filo: state.quote.filo,
        customerId: state.departureServices[0].customers[0].id,
        source: 'checkout',
        autoBalance: autoBalance
      })
      .then(response => {
        const paymentFrameData = {
          url: response.data.url
        };
        commit('setPaymentFrame', paymentFrameData);
        commit('setTransId', response.data.transId);
        commit('setPreviousAmountPaid', response.data.amountPaid ?? 0);
        commit('setPaymentLoading', false);
      })
      .catch(error => {
        commit('setError', true);
        commit('setErrorMessage', error.message);
        commit('setPaymentLoading', false);
      });
  },

  completeBooking({
    state,
    getters,
    commit
  }) {
    commit('setLoading', true);
    // Use direct state property (persisted in passengers action before fields were destroyed).
    // getters.roomPassengers.fields are deleted by Input.beforeUnmount when steps unmount.
    const leadPassenger = state.passengers.find(p => p.lead);
    const leadPaxEmail = leadPassenger?.email ?? getters.roomPassengers.find(p => p.lead)?.email;

    return axios
    .post(state.data.booking.completeBookingSubmitUrl, {
      bookingId: state.bookingData.id ?? state.bookingId,
      leadPaxEmail: leadPaxEmail
    })
    .then(response => {
      if(state.bookingType !== 'reserved') {
        commit('setBookingType', response.data.bookingDetails.type);
        //TODO: check if we will get info from FW if this booking is for new account
        //if (response.data && response.data.bookingDetails && response.data.bookingDetails.isNewAccount) {
        //  commit('setIsNewAccount', response.data.bookingDetails.isNewAccount); //set false as default value in state.js
        //}
      }
      commit('setLoading', false);
    })
    .catch(() => {
      commit('setLoading', false);
    });
  },
}
